import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, PenLineIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Field';
import { useNotes } from '../../contexts/NotesContext';

/**
 * Quick capture: a thought saved from anywhere in Campuzon without choosing a
 * folder, template, tag or course. Organisation happens later.
 */
export function QuickCapture() {
  const {
    quickCapture
  } = useNotes();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [savedId, setSavedId] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  function save() {
    if (!text.trim()) return;
    const id = quickCapture(text);
    setSavedId(id);
    setText('');
    window.setTimeout(() => {
      setSavedId(null);
      setOpen(false);
    }, 1400);
  }
  return <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Quick capture a note" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line-strong bg-surface px-2.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:border-ink-subtle hover:text-ink" data-id="element-3952" data-mp="13hlt6nfb7dta">
        <PenLineIcon className="h-4 w-4" data-id="element-3953" data-mp="orxtfqms0n7o" />
        <span className="hidden lg:inline" data-id="element-3954" data-mp="jvphem3vla4q">Capture</span>
      </button>

      <AnimatePresence data-id="element-3955" data-mp="ftccu10v7i9j">
        {open && <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center" data-id="element-3956" data-mp="funf1wenwhyj">
            <motion.div className="fixed inset-0 bg-ink/40" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease: 'easeOut'
        }} onClick={() => setOpen(false)} data-id="element-3957" data-mp="aw2e1url5u91" />
            <motion.div role="dialog" aria-modal="true" aria-label="Quick capture" className="relative z-10 flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-y-auto rounded-card border border-line bg-surface shadow-pop" initial={{
          opacity: 0,
          scale: 0.96,
          y: 8
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 4
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-3958" data-mp="140vn6q0d977">
              <header className="flex items-center justify-between border-b border-line px-5 py-3.5" data-id="element-3959" data-mp="a6a4ls5mouih">
                <p className="text-[15px] font-semibold text-ink" data-id="element-3960" data-mp="xvlafo1u5f3h">Quick note</p>
                <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-md p-1 text-ink-subtle hover:bg-canvas hover:text-ink" data-id="element-3961" data-mp="12nwacqwkt0w1">
                  <XIcon className="h-4 w-4" data-id="element-3962" data-mp="2ief1e4j9od5h" />
                </button>
              </header>

              <div className="px-5 py-4" data-id="element-3963" data-mp="2x2ry53q6i011">
                {savedId ? <div className="flex items-center gap-2.5 py-4" data-id="element-3964" data-mp="2s5jeeypdtnxo">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ok-700" data-id="element-3965" data-mp="24enoy88ef4tl">
                      <CheckIcon className="h-3.5 w-3.5 text-white" data-id="element-3966" data-mp="1zgcga0tub0cy" />
                    </span>
                    <p className="text-[13px] text-ink" data-id="element-3967" data-mp="2dfc7ng8qltct">
                      Saved to Notes, unfiled.{' '}
                      <button type="button" onClick={() => {
                  navigate('/notes');
                  setOpen(false);
                }} className="font-semibold text-brand-800 underline" data-id="element-3968" data-mp="29dh21zjrwtt8">
                        Open it
                      </button>
                    </p>
                  </div> : <>
                    <Textarea autoFocus rows={5} value={text} onChange={event => setText(event.target.value)} onKeyDown={event => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') save();
              }} placeholder="Write it down now, organise it later…" aria-label="Quick note text" data-id="element-3969" data-mp="symg8devyobm" />
                    <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-3970" data-mp="1gnxjc1jpkusx">
                      No folder, template or tag needed. The first line becomes the title.
                    </p>
                  </>}
              </div>

              {!savedId && <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3" data-id="element-3971" data-mp="1qk0nte2195j1">
                  <Button variant="ghost" onClick={() => setOpen(false)} data-id="element-3972" data-mp="365cc2lw1iklh">
                    Cancel
                  </Button>
                  <Button variant="primary" disabled={!text.trim()} onClick={save} data-id="element-3973" data-mp="315g6wjblwn4c">
                    Save note
                  </Button>
                </footer>}
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}